import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useDeleteApiPnrsByIdMutation,
  useGetApiPnrsByIdQuery,
  useGetApiUsersMeQuery,
  usePostApiPnrsByIdSubmitMutation,
} from '../../app/athenaApiGen';
import DynamicForm from '../../components/dynamic-form/DynamicForm';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import {
  createFormDataChangeHandler,
  parseRawFormData,
} from '../../dashboard/components/formUtils';
import useSchemaForm from '../../hooks/useSchemaForm';
import { pnrFormSchema } from './PNRFormSchema';

export default function PNRForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: meData } = useGetApiUsersMeQuery();
  const [formData, setFormData, isLoading, resetFormData] =
    useSchemaForm('PNRFormSchema');
  const { data: pnrResp, isFetching: isFetchingPNR } = useGetApiPnrsByIdQuery(
    { id: id || '' },
    { skip: !id },
  );
  const [deletePnr, { isLoading: deleting }] = useDeleteApiPnrsByIdMutation();
  const [submitPnr] = usePostApiPnrsByIdSubmitMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isCloneMode = searchParams.get('mode') === 'clone';
  const [savedPnrId, setSavedPnrId] = React.useState(isCloneMode ? null : (id || null)); // Store ID after save

  // Always start fresh on no-id route navigations like /pnr
  useEffect(() => {
    if (id) return;
    resetFormData();
    setSavedPnrId(null);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, [id, location.key, resetFormData]);

  const parsedRaw = useMemo(() => {
    const raw = pnrResp?.rawFormData || pnrResp?.RawFormData;
    return raw ? parseRawFormData(raw) : null;
  }, [pnrResp]);

  useEffect(() => {
    if (!id) return;
    if (!parsedRaw && pnrResp) {
      const normalized = { ...pnrResp };
      // Convert date fields to dayjs objects regardless of source
      if (normalized.pnrDate) normalized.pnrDate = dayjs(normalized.pnrDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      // Convert boolean values back to Yes/No strings for radio buttons
      if (typeof normalized.actionRequiredByGovernment === 'boolean') {
        normalized.actionRequiredByGovernment =
          normalized.actionRequiredByGovernment ? 'Yes' : 'No';
      }
      if (typeof normalized.problemImpactDelivery === 'boolean') {
        normalized.problemImpactDelivery = normalized.problemImpactDelivery
          ? 'Yes'
          : 'No';
      }
      if (typeof normalized.deliveryScheduleRecoverable === 'boolean') {
        normalized.deliveryScheduleRecoverable =
          normalized.deliveryScheduleRecoverable ? 'Yes' : 'No';
      }
      if (typeof normalized.increasedCostAnticipated === 'boolean') {
        normalized.increasedCostAnticipated =
          normalized.increasedCostAnticipated ? 'Yes' : 'No';
      }
      normalized.workflowState = isCloneMode ? '' : (pnrResp.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
      return;
    }
    if (parsedRaw) {
      const normalized = { ...parsedRaw };
      if (normalized.pnrDate) normalized.pnrDate = dayjs(normalized.pnrDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      // Convert boolean values back to Yes/No strings for radio buttons
      if (typeof normalized.actionRequiredByGovernment === 'boolean') {
        normalized.actionRequiredByGovernment =
          normalized.actionRequiredByGovernment ? 'Yes' : 'No';
      }
      if (typeof normalized.problemImpactDelivery === 'boolean') {
        normalized.problemImpactDelivery = normalized.problemImpactDelivery
          ? 'Yes'
          : 'No';
      }
      if (typeof normalized.deliveryScheduleRecoverable === 'boolean') {
        normalized.deliveryScheduleRecoverable =
          normalized.deliveryScheduleRecoverable ? 'Yes' : 'No';
      }
      if (typeof normalized.increasedCostAnticipated === 'boolean') {
        normalized.increasedCostAnticipated =
          normalized.increasedCostAnticipated ? 'Yes' : 'No';
      }
      normalized.workflowState = isCloneMode ? '' : (pnrResp?.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
    }
  }, [id, isCloneMode, parsedRaw, pnrResp, setFormData]);

  if (isLoading || (id && isFetchingPNR && !parsedRaw)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleFormDataChange = createFormDataChangeHandler(setFormData);

  // Default to view mode when editing an existing record (id present),
  // unless explicitly set to edit via ?mode=edit.
  // Also force view mode if workflow has progressed past Pending.
  const editableWorkflow = !formData?.workflowState || formData.workflowState === 'Pending';
  const isViewMode = isCloneMode ? false : (id ? searchParams.get('mode') !== 'edit' || !editableWorkflow : false);

  // Handle cancel — act like the browser back button so callers from the
  // approval queue or elsewhere land back on the page they came from
  // instead of being forced to the PNR list.
  const handleCancel = () => {
    navigate(-1);
  };

  // Handle successful submission - navigate back to list
  const handleSubmitSuccess = (result) => {
    if (result.ok && result.payload) {
      // Store the returned ID for subsequent saves
      if (result.payload.pnrId) {
        setSavedPnrId(result.payload.pnrId);
      }
    }
    if (result.ok && !result.skipNavigation) {
      setTimeout(() => navigate('/pnr-list'), 2500);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePnr({ id: id }).unwrap();
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'PNR deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/pnr-list'), 2500);
    } catch (error) {
      console.error('Failed to delete PNR', error);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Failed to delete PNR. Please try again.',
        severity: 'error',
      });
    }
  };

  const canDelete = id && editableWorkflow && formData?.workflowState !== undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '100%' } }}>
      <DynamicForm
        schema={pnrFormSchema}
        formData={formData}
        setFormData={handleFormDataChange}
        onSubmit={handleSubmitSuccess}
        showSubmit={isCloneMode || !savedPnrId}
        isEditMode={!isCloneMode && Boolean(savedPnrId)}
        recordId={isCloneMode ? null : savedPnrId}
        readOnly={isViewMode}
        onCancel={handleCancel}
        onDelete={canDelete ? handleDeleteClick : undefined}
        submitMutation={submitPnr}
        authorId={meData?.contact?.contactId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete PNR"
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `
            Are you sure you want to delete PNR <strong>${formData?.pnrNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
